<script setup lang='ts'>
import { Heart } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Product } from '@/types/products';

import BrittanyIcon from '@/components/labels/BrittanyIcon.vue';
import EcoFriendlyIcon from '@/components/labels/EcoFriendlyIcon.vue';
import { useFavorites } from '@/composables/useFavorites.js';
import { useProductLink } from '@/composables/useProductLink';
import { vGtmTrack } from '@/directives/v-gtm-track.js';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { Language } from '@/types/locale';
import {
  convertUrlToResizer,
  formatActivityDuration,
  formatDisplayRating,
  isEcoFriendly,
  isProducedInBrittany,
} from '@/utils/utils.js';

import TheCardGallery from './TheCardGallery.vue';

const props = withDefaults(
  defineProps<{
    responsiveRow: boolean;
    product: Product;
    showFavorite?: boolean;
    prefixUrl: string | null;
    small?: boolean;
    showCloseButton?: boolean;
    withSlider?: boolean;
    hasLargeViewport?: boolean;
    persistedProductGalleryId?: string;
  }>(),
  {
    showFavorite: true,
    showCloseButton: false,
    withSlider: false,
    hasLargeViewport: false,
  },
);

const emit = defineEmits(['closePopup', 'persistedGallery']);

const { handleAddCrossSellingToFavorite } = useFavorites();
const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration, currentLocale, componentsBaseUrl } = storeToRefs(globalConfigurationStore);

const { t } = useI18n();
const { properties: _, ...rest } = props.product;

const isFavorite = ref<boolean>(false);
const imageSrc = ref<string>(props.product.image ?? props.product.images?.[0]?.url ?? 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png');

const galleryButtonsPersisted = computed(() => props.persistedProductGalleryId === props.product.id);

const { productLink } = useProductLink(() => props.product, () => props.prefixUrl);
const externalLink = computed(() => productLink.value ?? '');
const duration = computed(() => props.product.properties.find(it => it.propertyName === 'nextSessionDate')?.value?.duration ?? 0);
const computedDuration = computed(() => {
  const numberOfDays = props.product.properties.find(it => it.propertyName === 'nextSessionDate')?.value?.numberOfDays ?? 0;
  if (numberOfDays > 1) {
    return `${numberOfDays} jours`;
  }
  else {
    return formatActivityDuration(duration.value);
  }
});

const rating = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.rating ?? null);
const displayedRating = ref(formatDisplayRating(rating.value ?? 0));

const ratingReviewCount = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.ratingReviewCount ?? null);
const ratingSource = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.source ?? null);

const locality = `${props.product.location?.locality}`;

const imageLocalization = props.product.importedInLangs.includes(currentLocale.value as any) ? currentLocale.value : Language.FRENCH;
const imagesToDisplay = computed(() => {
  let images;
  images = props.product.images?.filter((it: any) => it.lang === imageLocalization).slice(0, 6);
  if (!images.length) {
    images = props.product.images?.filter((it: any) => it.lang === null).slice(0, 6);
  }
  return images;
});

const hasMarketingTag = computed(() => 'marketingTag' in props.product);
const marketingTagLabel = computed(() => props.product.marketingTag?.name.find(it => it.lang === currentLocale.value)?.value);

function handleImgError() {
  imageSrc.value = 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png';
}

function handleFavorite(event: Event) {
  handleAddCrossSellingToFavorite(event, isFavorite);
}

function openDetails(link: string) {
  const isDiffusionMode = !!globalConfiguration.value?.diffusion;
  if (isDiffusionMode) {
    const url = new URL(window.location.href);
    const id = props.product.id;
    url.searchParams.set('product', id);
    window.open(url.toString(), '_blank');
  }
  else {
    window.open(link, '_blank');
  }
}

function handleClose(event: Event) {
  event.preventDefault();
  event.stopPropagation();
  emit('closePopup');
}
</script>

<template>
  <section
    v-gtm-track:card="{ product: rest }" class="card_wrapper"
    :class="{ 'row': props.responsiveRow, 'small-card': props.small }" :aria-label="t('card.label')"
  >
    <button
      v-if="showCloseButton" type="button" class="close-button" aria-label="Fermer" @click="handleClose"
      @keydown.enter="handleClose"
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="Icon">
          <path
            id="Vector" d="M14.6666 5.33301L5.33325 14.6663M5.33325 5.33301L14.6666 14.6663" stroke="black"
            stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
          />
        </g>
      </svg>
    </button>
    <section class="card_header">
      <div v-if="isEcoFriendly(product)" class="eco-friendly-badge">
        <EcoFriendlyIcon />
      </div>
      <div v-if="isProducedInBrittany(product)" class="brittany-badge">
        <BrittanyIcon />
      </div>
      <div
        v-if="hasMarketingTag && marketingTagLabel && props.responsiveRow" class="marketing-tag"
        :style="{ '--tagText': props.product.marketingTag?.textColor, '--tagColor': props.product.marketingTag?.backgroundColor, 'left': (isEcoFriendly(product) || isProducedInBrittany(product)) ? '3rem' : '0.75rem' }"
      >
        {{ marketingTagLabel }}
      </div>
      <img
        v-if="!props.withSlider || (props.withSlider && !props.hasLargeViewport && props.responsiveRow) || imagesToDisplay.length < 2"
        :src="convertUrlToResizer(imageSrc)" alt="lieu de l'activité" loading="lazy" @error="handleImgError"
        @click="openDetails(externalLink)"
      >
      <TheCardGallery
        v-else
        :images="imagesToDisplay" :link="externalLink"
        :buttons-persisted="galleryButtonsPersisted"
        :product-id="props.product.id"
        @is-persisted="emit('persistedGallery', props.product.id)"
      />
      <button
        v-if="showFavorite" id="card_favorite" type="button" :data-id="props.product.id" class="favorite"
        :aria-label="t('card.favorite')" @click="handleFavorite" @keydown.enter="handleFavorite"
      >
        <Heart :class="{ checked: isFavorite }" aria-hidden="true" />
      </button>
    </section>
    <section class="infos_wrapper" :aria-label="t('card.infos')" @click="openDetails(externalLink)">
      <div class="flex_wrapper">
        <p class="small_text">
          {{ locality }}
        </p>
      </div>
      <h3 class="title_text_clamp">
        {{ props.product.name ?? 'No name found.' }}
      </h3>
      <div v-if="duration" class="flex_wrapper spacing_bottom">
        <p class="small_text">
          {{ computedDuration }}
        </p>
      </div>
      <div
        v-if="hasMarketingTag && marketingTagLabel" class="mobile-marketing-tag"
        :class="{ 'marketing-responsive': props.responsiveRow }"
        :style="{ '--tagText': props.product.marketingTag?.textColor, '--tagColor': props.product.marketingTag?.backgroundColor }"
      >
        {{ marketingTagLabel }}
      </div>
      <div v-if="rating" class="rating">
        <svg class="external-star" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
        <p class="small_text">
          {{ displayedRating }}
        </p>
        <span v-if="ratingReviewCount" class="review-count">({{ ratingReviewCount }})</span>
        <img v-if="ratingSource === 'GOOGLE'" class="provider-logo" :src="`${componentsBaseUrl}/logo_google.jpg`" alt="source des avis" loading="lazy">
      </div>
      <p class="price_tag">
        {{ t('card.from') }}
        <span
          :style="[props.product.displayedPrice.hasDiscount ? 'text-decoration: line-through; font-weight: var(--yb-text-light); color:var(--yb-color-black)' : 'color:var(--yb-color-black);']"
        >
          {{ props.product.displayedPrice.originalPrice }}
        </span>&nbsp;
        <span
          v-if="props.product.displayedPrice.hasDiscount"
          style="color: var(--yb-color-primary)"
        >{{ props.product.displayedPrice.priceFrom }}</span>
      </p>
    </section>
  </section>
</template>

<style lang="css" scoped>
.card_wrapper {
  --img-ratio: 4/3;

  all: unset;
  display: flex;
  flex-direction: column;
  background-color: var(--yb-color-white);
  border: 1px solid #ccc;
  border-radius: var(--yb-card-radius);

  overflow: hidden;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--yb-outline);
    outline-offset: 2px;
  }

  .card_header {
    container: card / inline-size;
    position: relative;
    width: 100%;
    /* overflow: clip;
    overflow: hidden; */

    @media screen and (width < 768px) {
      height: 100%;
      /* aspect-ratio: none; */
    }

    @media screen and (width >= 768px) {
      aspect-ratio: var(--img-ratio);
    }
  }

  .marketing-tag {
    position: absolute;
    z-index: 8;
    top: 0.75rem;
    background-color: var(--tagColor);
    color: var(--tagText);
    border-radius: 1rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    font-weight: bold;
    @media screen and (width < 768px) {
      display: none;
    }
    @container card (width < 200px) {
      display: none;
    }
  }

  .mobile-marketing-tag {
    position: relative;
    top: 2px;
    background-color: var(--tagColor);
    color: var(--tagText);
    border-radius: 1rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    font-weight: bold;
    margin-top: auto;
    @container card (width > 200px) {
      display: none;
    }
  }

  .marketing-responsive {
    @media screen and (width >= 768px) {
      display: none;
    }
  }

  img {
    width: 100%;
    aspect-ratio: var(--img-ratio);
    object-fit: cover;
    object-position: center;
  }

  .favorite {
    position: absolute;
    display: flex;
    background-color: transparent;
    border: none;
    padding: 0;
    top: 10px;
    right: 10px;
    z-index: 5;
    transition: scale 0.3s ease-out;

    &:focus-visible {
      outline: 2px solid var(--yb-outline);
    }

    svg {
      stroke: var(--yb-color-white);
      stroke-width: 2px;
      fill: rgba(0, 0, 0, 0.4);
      cursor: pointer;
    }

    svg.checked {
      stroke: var(--yb-color-primary);
      stroke-width: 2px;
      fill: var(--yb-color-primary);
    }

    @media (prefers-reduced-motion: no-preference) {
      &:hover {
        scale: 1.1;
      }
    }
  }

  .brittany-badge,
  .eco-friendly-badge {
    position: absolute;
    display: flex;
    background-color: var(--yb-color-ecofriendly);
    padding: 0.5rem;
    top: 0;
    left: 0.5rem;
    border-radius: 0 0 var(--yb-radius-sm) var(--yb-radius-sm);
    z-index: 5;
  }

  .brittany-badge {
    background-color: transparent;
    padding: 0.125rem;
    top: 0.25rem;
  }

  .flex_wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .spacing_top {
    margin-top: var(--yb-spacing-sm);
  }

  .spacing_bottom {
    margin-bottom: var(--yb-spacing-xs);
  }

  .infos_wrapper {
    all: unset;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: var(--yb-spacing-xs);
    padding: 0.8rem;
    min-height: 180px;
  }

  .rating {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--yb-color-black);
    margin-top: auto;

    p {
      font-weight: var(--yb-text-bold) !important;
    }

    .review-count {
      font-weight: var(--yb-text-light);
      font-size: var(--yb-text-sm);
    }

    .external-star {
      width: 18px;
      height: 18px;
      fill: #ff9800;
      flex-shrink: 0;
    }

    .provider-logo {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      object-fit: contain;
    }
  }

  .title_text_clamp {
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-size: var(--yb-text-base);
    font-weight: var(--yb-text-semi-bold);
    color: var(--yb-card-title-color);
    line-height: var(--yb-text-lineheight);
    transition: color 0.3s ease-out;

    @media screen and (width < 768px) {
      font-size: var(--yb-text-base);
    }
  }

  .small_text {
    color: var(--yb-color-black);
    font-size: var(--yb-text-sm);
    line-height: var(--yb-text-lineheight);

    @media screen and (width < 768px) {
      font-size: var(--yb-text-xs);
    }
  }

  .badge_new {
    max-width: fit-content;
    background-color: var(--yb-color-lightgray);
    color: var(--yb-color-black);
    font-size: var(--yb-text-xs);
    font-weight: var(--yb-text-bold);
    padding: var(--yb-spacing-xs);
    border-radius: var(--yb-radius-sm);

    @media screen and (width >= 768px) {
      margin-top: auto;
    }
  }

  .price_tag {
    font-size: var(--yb-text-base);
    font-weight: var(--yb-text-semi-bold);
    color: var(--yb-color-black);
    margin-top: auto;
  }

  .rating + .price_tag {
    margin-top: 0;
  }

  @media screen and (width < 768px) {
    .mobile-marketing-tag + .rating,
    .mobile-marketing-tag + .price_tag {
      margin-top: 0;
    }
  }

  &:hover {
    .title_text_clamp {
      color: var(--yb-color-primary);
    }
  }
}

.small-card {
  display: grid;
  max-width: 100%;
  grid-template-columns: 1fr 2fr;
  grid-template-rows: auto;
  grid-auto-flow: row;
  grid-template-areas: 'image infos';
  gap: var(--yb-spacing-sm);
  height: var(--yb-small-card-height);
  min-height: var(--yb-small-card-height);

  .card_header {
    height: 100%;
    aspect-ratio: unset;
  }

  img {
    height: 100%;
    object-fit: cover;
  }

  .infos_wrapper {
    overflow: hidden;
    min-height: 0;
  }

  .favorite {
    top: 0.5rem;
    right: 0.5rem;
  }

  .close-button {
    position: absolute;
    background-color: var(--yb-color-white);
    border: none;
    top: var(--yb-spacing-xs);
    right: var(--yb-spacing-xs);
    z-index: 10;
  }

  .spacing_top {
    margin-top: 0;
  }

  .title_text_clamp {
    font-size: var(--yb-text-base);
  }

  .small_text {
    font-size: var(--yb-text-xs);
  }
}

@media screen and (width < 768px) {
  .card_wrapper.row {
    display: grid;
    max-width: 100%;
    grid-template-columns: 1fr 1.5fr;
    grid-template-rows: auto;
    grid-auto-flow: row;
    grid-template-areas: 'image infos';
    /* gap: var(--yb-spacing-sm); */

    img {
      grid-area: image;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .favorite {
      top: 0.5rem;
      right: 0.5rem;
    }

    .spacing_top {
      margin-top: 0;
    }

    .title_text_clamp {
      font-size: var(--yb-text-base);
    }

    .small_text {
      font-size: var(--yb-text-xs);
    }
  }
}
</style>
